import { useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useModalContext } from './ModalProvider';
import StripePaymentForm from './StripePaymentForm';
import { userAPI } from '../UserAPI';

import { Check, Info, Loader2, Plus, Trash2 } from 'lucide-react';

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";

interface StripePaymentMethodProps {
    customerId: string;
    onPaymentMethodAdded?: (paymentMethod: any) => void;
    onPaymentMethodRemoved?: (paymentMethodId: string) => void;
}

interface PaymentMethod {
    id: string;
    type: string;
    card?: {
        brand: string;
        last4: string;
        exp_month: number;
        exp_year: number;
    };
    billing_details?: {
        name: string;
        email: string;
    };
}

export default function StripePaymentMethod({ customerId, onPaymentMethodAdded, onPaymentMethodRemoved }: StripePaymentMethodProps) {
    const { t: _t } = useTranslation();
    const { pushError } = useModalContext() as any;
    const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
    const [loading, setLoading] = useState(false);
    const [showAddForm, setShowAddForm] = useState(false);
    const [defaultPaymentMethod, setDefaultPaymentMethod] = useState<string | null>(null);
    const [formLoading, setFormLoading] = useState(false);

    useEffect(() => {
        loadPaymentMethods();
    }, [customerId]);

    const loadPaymentMethods = async () => {
        try {
            setLoading(true);
            const data = await userAPI.getPaymentMethods(customerId) as any;
            if (data) {
                setPaymentMethods(data.paymentMethods || []);
                setDefaultPaymentMethod(data.defaultPaymentMethod);
            } else {
                // Handle case where API returns null/undefined
                setPaymentMethods([]);
                setDefaultPaymentMethod(null);
            }
        } catch (error) {
            console.error('Error loading payment methods:', error);
            // Don't show error to user for initial load, just set empty state
            setPaymentMethods([]);
            setDefaultPaymentMethod(null);
        } finally {
            setLoading(false);
        }
    };

    const handlePaymentMethodAdded = (newPaymentMethod: PaymentMethod) => {
        setPaymentMethods(prev => [...prev, newPaymentMethod]);
        onPaymentMethodAdded?.(newPaymentMethod);
        setShowAddForm(false);
        setFormLoading(false);

        // Show success message for temporary payment methods
        if ((newPaymentMethod as any)._temp) {
            console.log('✅ Payment method added successfully (temporary - backend not implemented)');
        }
    };

    const removePaymentMethod = async (paymentMethodId: string) => {
        try {
            setLoading(true);
            await userAPI.removePaymentMethod(customerId, paymentMethodId);
            setPaymentMethods(prev => prev.filter(pm => pm.id !== paymentMethodId));
            onPaymentMethodRemoved?.(paymentMethodId);
        } catch (error) {
            console.error('Error removing payment method:', error);
            pushError(new Error('Failed to remove payment method. Please try again.'));
        } finally {
            setLoading(false);
        }
    };

    const setDefaultPaymentMethodHandler = async (paymentMethodId: string) => {
        try {
            setLoading(true);
            await userAPI.setDefaultPaymentMethod(customerId, paymentMethodId);
            setDefaultPaymentMethod(paymentMethodId);
        } catch (error) {
            console.error('Error setting default payment method:', error);
            pushError(new Error('Failed to set default payment method. Please try again.'));
        } finally {
            setLoading(false);
        }
    };

    const formatCardNumber = (last4: string) => `•••• •••• •••• ${last4}`;
    const formatExpiry = (month: number, year: number) => `${month.toString().padStart(2, '0')}/${year}`;

    if (loading && paymentMethods.length === 0) {
        return (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 aria-hidden className="h-4 w-4 animate-spin" />
                Lade Zahlungsmethoden...
            </div>
        );
    }

    return (
        <div>
            <div className="mb-5 flex items-center justify-between">
                <Button
                    type="button"
                    variant="ghost"
                    className="gap-2"
                    onClick={() => setShowAddForm(true)}
                    disabled={loading}
                >
                    <Plus className="h-4 w-4" aria-hidden />
                    Zahlungsmethode hinzufügen
                </Button>
            </div>

            {paymentMethods.length === 0 && !loading && (
                <Alert variant="info">
                    <Info aria-hidden className="h-4 w-4" />
                    <AlertDescription>
                        Keine Zahlungsmethoden hinterlegt. Fügen Sie eine Kreditkarte oder Bankverbindung hinzu.
                    </AlertDescription>
                </Alert>
            )}

            {paymentMethods.map((paymentMethod) => (
                <div key={paymentMethod.id} className="mb-2 rounded-md border p-4">
                    <div className="flex flex-wrap items-center justify-between gap-4">
                        <div className="flex min-w-0 flex-wrap items-center gap-4">
                            {paymentMethod.card && (
                                <div className="flex min-w-0 flex-wrap items-center gap-4">
                                    <span className="text-sm">
                                        {paymentMethod.card.brand.toUpperCase()} {formatCardNumber(paymentMethod.card.last4)}
                                        {(paymentMethod as any)._temp && (
                                            <span className="ml-2 font-bold text-warning">(Temporär)</span>
                                        )}
                                    </span>
                                    <span className="text-sm text-muted-foreground">
                                        Gültig bis {formatExpiry(paymentMethod.card.exp_month, paymentMethod.card.exp_year)}
                                    </span>
                                    {paymentMethod.billing_details?.name && (
                                        <span className="text-sm text-muted-foreground">
                                            {paymentMethod.billing_details.name}
                                        </span>
                                    )}
                                    {(paymentMethod as any)._temp && (
                                        <span className="text-sm text-warning">
                                            ⚠️ Backend API noch nicht implementiert - wird nicht gespeichert
                                        </span>
                                    )}
                                </div>
                            )}
                            {paymentMethod.type === 'sepa_debit' && (
                                <div>
                                    <span className="text-sm">SEPA-Lastschrift</span>
                                    <span className="text-sm text-muted-foreground">
                                        Bankkonto hinterlegt
                                    </span>
                                </div>
                            )}
                        </div>
                        <div className="flex items-center gap-4">
                            {defaultPaymentMethod === paymentMethod.id && (
                                <span className="text-sm font-bold text-success">
                                    Standard
                                </span>
                            )}
                            {defaultPaymentMethod !== paymentMethod.id && (
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    aria-label="Als Standard festlegen"
                                    onClick={() => setDefaultPaymentMethodHandler(paymentMethod.id)}
                                    disabled={loading}
                                >
                                    <Check className="h-4 w-4" aria-hidden />
                                </Button>
                            )}
                            <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                aria-label="Zahlungsmethode entfernen"
                                onClick={() => removePaymentMethod(paymentMethod.id)}
                                disabled={loading}
                            >
                                <Trash2 className="h-4 w-4" aria-hidden />
                            </Button>
                        </div>
                    </div>
                </div>
            ))}

            <Dialog
                open={showAddForm}
                onOpenChange={(open) => {
                    if (!open) {
                        setShowAddForm(false);
                    }
                }}
            >
                {/* 600px wie im abgelösten Sass-Modul (.wide-dialog). */}
                <DialogContent className="max-h-[85vh] w-[calc(100vw-2rem)] overflow-y-auto sm:max-w-[600px]">
                    <DialogHeader>
                        <DialogTitle>Zahlungsmethode hinzufügen</DialogTitle>
                        <DialogDescription>Fügen Sie eine neue Kreditkarte oder Bankverbindung hinzu.</DialogDescription>
                    </DialogHeader>
                    <div className="py-5">
                        <StripePaymentForm
                            customerId={customerId}
                            onSuccess={handlePaymentMethodAdded}
                            onCancel={() => setShowAddForm(false)}
                            onLoadingChange={setFormLoading}
                        />
                    </div>
                    <DialogFooter className="gap-2 sm:gap-0">
                        <Button type="button" variant="outline" onClick={() => setShowAddForm(false)}>Abbrechen</Button>
                        <Button
                            type="button"
                            onClick={() => {
                                // Trigger form submission
                                const form = document.querySelector('form');
                                if (form) {
                                    const submitEvent = new Event('submit', { bubbles: true, cancelable: true });
                                    form.dispatchEvent(submitEvent);
                                }
                            }}
                            disabled={formLoading}
                        >
                            {formLoading && <Loader2 aria-hidden className="mr-2 h-4 w-4 animate-spin" />}
                            {formLoading ? 'Wird verarbeitet...' : 'Zahlungsmethode hinzufügen'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
